import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

// Import translations
import plTranslations from '../i18n/locales/pl.json';
import enTranslations from '../i18n/locales/en.json';
import ruTranslations from '../i18n/locales/ru.json';

export type Language = 'pl' | 'en' | 'ru';

type TranslationValue = string | { [key: string]: TranslationValue };
type Translations = { [key: string]: TranslationValue };

const translations: Record<Language, Translations> = {
  pl: plTranslations,
  en: enTranslations,
  ru: ruTranslations,
};

export const languageNames: Record<Language, string> = {
  pl: 'Polski',
  en: 'English',
  ru: 'Русский',
};

export const languageFlags: Record<Language, string> = {
  pl: '🇵🇱',
  en: '🇬🇧',
  ru: '🇺🇦',
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  availableLanguages: Language[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'app_language';

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // Get language from localStorage or default to Polish
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && (stored === 'pl' || stored === 'en' || stored === 'ru')) {
      return stored as Language;
    }
    return 'pl';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
    // Update HTML lang attribute
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
  }, []);

  // Translation function with support for nested keys (e.g., 'nav.myProduction')
  // and parameter interpolation (e.g., '{{progress}}')
  const t = useCallback((key: string, params?: Record<string, string | number>): string => {
    const keys = key.split('.');
    let value: TranslationValue = translations[language];

    for (const k of keys) {
      if (typeof value === 'object' && value !== null && k in value) {
        value = value[k];
      } else {
        // Fallback to Polish, then to the key itself
        let fallbackValue: TranslationValue = translations['pl'];
        for (const fk of keys) {
          if (typeof fallbackValue === 'object' && fallbackValue !== null && fk in fallbackValue) {
            fallbackValue = fallbackValue[fk];
          } else {
            return key; // Key not found in fallback either
          }
        }
        value = fallbackValue;
        break;
      }
    }

    if (typeof value !== 'string') {
      return key;
    }

    // Replace parameters like {{progress}} with actual values
    if (params) {
      return value.replace(/\{\{(\w+)\}\}/g, (_, paramKey) => {
        return params[paramKey]?.toString() ?? `{{${paramKey}}}`;
      });
    }

    return value;
  }, [language]);

  const availableLanguages: Language[] = ['pl', 'en', 'ru'];

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        t,
        availableLanguages,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
